/**
 * Error thrown by HttpService for a non-2xx response or a failed fetch.
 *
 * Carries what the server actually said - the API's auth endpoints answer
 * every rejection with `{ code, message, details?, request_id }` - so a
 * screen can explain the failure and offer the right next step instead of
 * one catch-all string. `status` is 0 when the request never got a
 * response (offline, DNS, CORS, server down).
 */
export class HttpError extends Error {
  constructor(message, { status = 0, code, details, requestId, url } = {}) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.code = code || null;
    this.details = details || null;
    this.requestId = requestId || null;
    this.url = url || null;
  }
}

/** `code` when fetch itself failed - nothing came back at all. */
export const NETWORK_ERROR = "NETWORK_ERROR";
/** `code` for a 429 from the API's rate limiter (its body has no `code`). */
export const RATE_LIMITED = "RATE_LIMITED";

export const hostOf = (url) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/**
 * Build an HttpError from a non-ok Response. Reads the body once as text so
 * an empty or non-JSON body still produces a usable error.
 */
export async function httpErrorFromResponse(response, url) {
  let body = null;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  const status = response.status;
  const code =
    body?.code ||
    (status === 429 ? RATE_LIMITED : `HTTP_${status}`);
  const message =
    body?.message || body?.error || `HTTP error! Status: ${status}`;
  return new HttpError(message, {
    status,
    code,
    details: body?.details,
    // Auth endpoints put it in the body; every API response also carries
    // it as a header (exposed via CORS) for bodies that don't.
    requestId: body?.request_id || response.headers.get("x-request-id"),
    url,
  });
}

/** Wrap a fetch() rejection (TypeError: Failed to fetch, abort, ...). */
export function httpErrorFromFetchFailure(error, url) {
  if (error instanceof HttpError) return error;
  return new HttpError(`Could not reach ${hostOf(url)}.`, {
    status: 0,
    code: NETWORK_ERROR,
    details: { reason: error?.message || String(error) },
    url,
  });
}
